/**
 * Banner ámbar con diagnóstico de facturas pagadas con error de sync a
 * Alegra. Muestra desglose por tipo (sin forma de pago, 401/403, 400,
 * otros) + botones "Auto-arreglar" y "Reintentar todo" + chips para
 * reintentar individualmente.
 *
 * Extraído de CxPPanel.jsx en Iter 236.m.14 (Carlos). Cobertura E2E:
 * specs/CxPPanel.spec.ts (`banner-cxp-sync-errors`, `btn-auto-fix-sync-errors`,
 * `btn-reintentar-todos-cxp`).
 */
import { Button } from '../../ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

const fmt = (n) => `$${Math.round(n || 0).toLocaleString('es-CL')}`;

export function SyncErrorsBanner({
  facturas,
  autoFixing,
  retryingAll,
  onAutoFix,
  onRetryAll,
  onRetryOne,
}) {
  if (!facturas || facturas.length === 0) return null;
  const sinForma = facturas.filter((f) => !f.forma_pago_v2);
  const con401 = facturas.filter((f) =>
    /401|403|unauthor|credencial|token/i.test(String(f.alegra_sync_error || '')),
  );
  const con400 = facturas.filter((f) =>
    /400|bad request/i.test(String(f.alegra_sync_error || '')) && f.forma_pago_v2,
  );
  const otros = facturas.length - sinForma.length - con401.length - con400.length;

  return (
    <div
      className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 flex items-start gap-3"
      data-testid="banner-cxp-sync-errors"
    >
      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
          {facturas.length} factura(s) pagada(s) con error de sync a Alegra
        </p>
        <p className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
          Pagadas en el ERP pero el pago no quedó registrado en Alegra.
        </p>
        <div className="text-xs text-amber-900 dark:text-amber-200 mt-1.5 space-y-0.5">
          {sinForma.length > 0 && (
            <p data-testid="diag-sin-forma">
              • <b>{sinForma.length}</b> sin <b>forma de pago</b> definida → edita cada factura,
              selecciona método (efectivo/transferencia/tarjeta) y luego "Reintentar".
            </p>
          )}
          {con401.length > 0 && (
            <p data-testid="diag-401">
              • <b>{con401.length}</b> con error <b>401/403</b> → revisa credenciales Alegra
              en Configuración → Alegra.
            </p>
          )}
          {con400.length > 0 && (
            <p data-testid="diag-400">
              • <b>{con400.length}</b> con <b>400 Bad Request</b> → revisa que la cuenta
              bancaria default exista en Alegra (config `alegra_payment_config.account_id`).
            </p>
          )}
          {otros > 0 && (
            <p data-testid="diag-otros">
              • <b>{otros}</b> con otros errores (timeout / red / sin alegra_id).
            </p>
          )}
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1">
          {facturas.slice(0, 6).map((f) => (
            <button
              type="button"
              key={f.id}
              onClick={() => onRetryOne(f)}
              className={`inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded font-mono hover:underline ${
                !f.forma_pago_v2
                  ? 'bg-rose-100 dark:bg-rose-900/50 hover:bg-rose-200'
                  : 'bg-amber-100 dark:bg-amber-900/50 hover:bg-amber-200'
              }`}
              title={f.alegra_sync_error || 'Error Alegra'}
              data-testid={`chip-retry-cxp-${f.id}`}
            >
              {f.folio || '—'}{' '}
              <span className="text-amber-700 dark:text-amber-300">· {fmt(f.monto)}</span>
              {!f.forma_pago_v2 && <span className="text-rose-700 dark:text-rose-300">⚠</span>}
            </button>
          ))}
          {facturas.length > 6 && (
            <span className="text-[11px] text-amber-700 dark:text-amber-300">
              +{facturas.length - 6} más…
            </span>
          )}
        </div>
      </div>
      <div className="flex-shrink-0 flex flex-col gap-1">
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs border-emerald-500 text-emerald-700 hover:bg-emerald-50"
          onClick={onAutoFix}
          disabled={autoFixing || retryingAll}
          data-testid="btn-auto-fix-sync-errors"
          title="Detecta facturas que YA están pagadas en Alegra y limpia el error sin pushear payment duplicado"
        >
          <RefreshCw className={`w-3 h-3 mr-1 ${autoFixing ? 'animate-spin' : ''}`} />
          {autoFixing ? 'Auto-arreglando…' : 'Auto-arreglar'}
        </Button>
        <Button
          size="sm"
          className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
          onClick={onRetryAll}
          disabled={retryingAll || autoFixing}
          data-testid="btn-reintentar-todos-cxp"
        >
          <RefreshCw className={`w-3 h-3 mr-1 ${retryingAll ? 'animate-spin' : ''}`} />
          {retryingAll ? 'Reintentando…' : 'Reintentar todo'}
        </Button>
      </div>
    </div>
  );
}
